import dayjs from 'dayjs';
import _ from 'lodash';

import { ValueStub } from 'constants/data';
import { MAX_INTEGER_VALUE, MAX_NUMBER_VALUE } from 'constants/number';
import { PHONE_NUMBER_FORMAT } from 'constants/ui';
import { jsonParse, jsonStringify } from 'utils/json';

import { removeExcessiveSpaces } from './string';

const PHONE_AREA_CODE_END = 3;
const PHONE_PREFIX_END = 6;
const PHONE_LINE_NUMBER_START = -4;
const ADDRESS_CITY_STATE_PARTS_COUNT = -2;

export const deleteByIndex = (list, index, count = 1) => {
  const newList = [...list];
  newList.splice(index, count);

  return newList;
};

export const getCleanPhoneNumber = value => value && value.replace('+1', '').replaceAll('-', '');

export const normalizeViewPhoneNumber = value => {
  if (!value || !value.startsWith('+1')) {
    return value;
  }

  value = getCleanPhoneNumber(value);

  return `${value.slice(0, PHONE_AREA_CODE_END)}-${value.slice(
    PHONE_AREA_CODE_END,
    PHONE_PREFIX_END,
  )}-${value.slice(PHONE_LINE_NUMBER_START)}`;
};

export const normalizeRequestPhoneNumber = value => (value ? `+1${value.replaceAll('-', '')}` : '');

export const normalizeViewSSN = ssn =>
  ssn?.replace(/^(.{1,3})(.{1,2})(.+)?.*$/, (__, p1, p2, p3) =>
    [p1, p2, p3].filter(Boolean).join('-'),
  );

export const normalizeBooleanStringToBoolean = str => {
  const formattedString = _.toLower(str);

  switch (formattedString) {
    case 'true':
      return true;

    case 'false':
      return false;

    default:
      return null;
  }
};

export const checkIsFieldEqualToValueInData = ({ data, value, fieldName }) => {
  const valueToCompare = removeExcessiveSpaces(value);

  const arrayData = _.isObject(data) ? Object.values(data) : data;

  return (arrayData || []).some(
    item => `${item[fieldName]}`.toLowerCase() === `${valueToCompare}`.toLowerCase(),
  );
};

export const checkIsNumberLessOrEqualMaxSafe = ({ value }) => value <= Number.MAX_SAFE_INTEGER;

export const checkIsNumberLessOrEqualMaxInteger = ({ value }) => value <= MAX_INTEGER_VALUE;

export const checkIsNumberLessOrEqualMaxDefault = ({ value }) => value <= MAX_NUMBER_VALUE;

export const setIn = (state, name, value) => {
  return _.setWith(_.clone(state), name, value, _.clone);
};

const resolveConditionalValue = valueOrGetter =>
  _.isFunction(valueOrGetter) ? valueOrGetter() : valueOrGetter;

export const getConditionalObject = (condition, objOrGetObj) =>
  condition ? resolveConditionalValue(objOrGetObj) : {};

export const getConditionalArray = (condition, arrayOrGetArray) =>
  condition ? resolveConditionalValue(arrayOrGetArray) : [];

export const getConditionalValue = (condition, value) => (condition ? value : null);

const formatWithPattern = (input, pattern) => {
  const escapedPattern = pattern.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
  const regexPattern = new RegExp(`^${escapedPattern.replace(/#/g, '\\d')}$`);

  if (regexPattern.test(input)) {
    return input;
  }

  const inputDigits = [...input].filter(char => !isNaN(parseInt(char)));

  return [...pattern].reduce(
    ({ result, digits }, char) => ({
      result: result + (char === '#' && digits.length ? digits.shift() : char),
      digits,
    }),
    { result: '', digits: inputDigits },
  ).result;
};

export const formatPhoneNumberForView = value =>
  value ? formatWithPattern(normalizeViewPhoneNumber(value), PHONE_NUMBER_FORMAT) : null;

export const replaceEmptyWithNull = (data, { includeNested = false } = {}) => {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => {
      const valueIfNotEmpty =
        includeNested && _.isPlainObject(value)
          ? replaceEmptyWithNull(value, includeNested)
          : value;

      return [key, _.isUndefined(value) || value === '' ? null : valueIfNotEmpty];
    }),
  );
};

export const replaceEmptyWithFalse = data =>
  Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      _.isUndefined(value) || value === '' ? false : value,
    ]),
  );

export const replaceFalsyValuesWithInitialValues = (data, initialData = data) =>
  Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      value || (_.isNull(initialData[key]) && null),
    ]),
  );

export const replaceStringedBooleanValueWithBoolean = value =>
  ({
    True: true,
    true: true,
    False: false,
    false: false,
  })[value] ?? value;

export const omitEmptyValues = obj =>
  _.reduce(
    obj,
    (acc, value, key) => {
      const cleaned = _.isPlainObject(value) ? omitEmptyValues(value) : value;
      const cleanedArray = _.isArray(value)
        ? value.map(i => (_.isPlainObject(i) ? omitEmptyValues(i) : i))
        : null;
      const result = _.isArray(value) ? cleanedArray : cleaned;

      if (_.isPlainObject(value) ? _.size(result) : result) {
        acc[key] = result;
      }

      return acc;
    },
    {},
  );

export const replaceStringedBooleanValuesWithBoolean = data =>
  Object.entries(data).reduce((acc, [key, value]) => {
    acc[key] = replaceStringedBooleanValueWithBoolean(value);

    return acc;
  }, {});

export const getOptionTypeName = (options, type) =>
  options.find(({ value }) => value === type)?.name ?? ValueStub.COMMON;

export const sortArrayByDate = (array, key, order) =>
  _.orderBy(array, [({ [key]: date }) => dayjs(date).valueOf()], [order]);

export const getPositiveValue = value => (value > 0 ? value : null);

export const getSplittedAddress = address => {
  const addressParts = address.split(',').map(line => line.trim());

  const addressLine2 = addressParts.slice(ADDRESS_CITY_STATE_PARTS_COUNT).join(', ');

  const addressLine1Parts = addressParts.slice(0, ADDRESS_CITY_STATE_PARTS_COUNT);
  const addressLine1 = addressLine1Parts.length > 0 ? addressLine1Parts.join(', ') : '';

  return [addressLine1, addressLine2];
};

export const trimBodyStringValues = body =>
  jsonParse(jsonStringify(body, (key, value) => value?.trim?.() ?? value));

export const prepareStringForComparison = value => value?.toLowerCase?.()?.trim();

export const alwaysTrue = () => true;

export const checkIsGuid = value => {
  const regex = /^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i;

  return regex.test(value);
};

export const getValueOrDash = value => value || '-';
